/** A palette index (0–15 standard/bright, 16–255 xterm cube/greys) or a `#rrggbb` truecolor. */
export type AnsiColor = number | `#${string}`;

export type AnsiStyle = {
  background: AnsiColor | null;
  bold: boolean;
  dim: boolean;
  foreground: AnsiColor | null;
  inverse: boolean;
  italic: boolean;
  strikethrough: boolean;
  underline: boolean;
};

export type AnsiSegment = { style: AnsiStyle; text: string };

const PLAIN_STYLE: AnsiStyle = {
  background: null,
  bold: false,
  dim: false,
  foreground: null,
  inverse: false,
  italic: false,
  strikethrough: false,
  underline: false,
};

// CSI (`ESC [ params final`), OSC (`ESC ] … BEL|ST`), or a lone two-byte escape. Only CSI `m`
// (SGR) changes the style; every other sequence (cursor moves, hyperlinks, …) is dropped.
const ESCAPE_PATTERN =
  // oxlint-disable-next-line no-control-regex -- matching terminal escape bytes is the point
  /\u001B(?:\[([0-9;:]*)([@-~])|\][^\u0007\u001B]*(?:\u0007|\u001B\\)?|[@-Z\\-_])/;
const SGR_SEPARATOR = /[;:]/;
const CUBE_LEVELS = [0, 95, 135, 175, 215, 255] as const;

function hex(value: number): string {
  return Math.max(0, Math.min(255, value)).toString(16).padStart(2, "0");
}

/** Reads an extended `38;5;n` / `38;2;r;g;b` color starting after the 38/48 code. */
function readExtendedColor(
  codes: readonly number[],
  start: number,
): { color: AnsiColor | null; next: number } {
  const mode = codes[start];
  if (mode === 5) {
    const index = codes[start + 1];
    return {
      color: index !== undefined && index >= 0 && index <= 255 ? index : null,
      next: start + 2,
    };
  }
  if (mode === 2) {
    const [r = 0, g = 0, b = 0] = codes.slice(start + 1, start + 4);
    return { color: `#${hex(r)}${hex(g)}${hex(b)}`, next: start + 4 };
  }
  return { color: null, next: start + 1 };
}

/** 30–37 / 40–47 set a standard color; 90–97 / 100–107 set its bright twin (index + 8). */
function readBasicColor(
  code: number,
): { index: number; target: "background" | "foreground" } | null {
  const bright = code >= 90;
  const base = bright ? code - 60 : code;
  const offset = bright ? 8 : 0;
  if (base >= 30 && base <= 37) {
    return { index: base - 30 + offset, target: "foreground" };
  }
  if (base >= 40 && base <= 47) {
    return { index: base - 40 + offset, target: "background" };
  }
  return null;
}

function applySgr(style: AnsiStyle, params: string): AnsiStyle {
  // `ESC[m` is a reset; colon sub-parameters (`38:2::r:g:b`) are read like semicolons.
  const codes = params === "" ? [0] : params.split(SGR_SEPARATOR).map(Number);
  let next = { ...style };
  for (let i = 0; i < codes.length; i++) {
    const code = codes[i] ?? 0;
    const basic = readBasicColor(code);
    if (basic !== null) {
      next[basic.target] = basic.index;
      continue;
    }
    switch (code) {
      case 0: {
        next = { ...PLAIN_STYLE };
        break;
      }
      case 1: {
        next.bold = true;
        break;
      }
      case 2: {
        next.dim = true;
        break;
      }
      case 3: {
        next.italic = true;
        break;
      }
      case 4: {
        next.underline = true;
        break;
      }
      case 7: {
        next.inverse = true;
        break;
      }
      case 9: {
        next.strikethrough = true;
        break;
      }
      case 22: {
        next.bold = false;
        next.dim = false;
        break;
      }
      case 23: {
        next.italic = false;
        break;
      }
      case 24: {
        next.underline = false;
        break;
      }
      case 27: {
        next.inverse = false;
        break;
      }
      case 29: {
        next.strikethrough = false;
        break;
      }
      case 39: {
        next.foreground = null;
        break;
      }
      case 49: {
        next.background = null;
        break;
      }
      case 38:
      case 48: {
        const { color, next: after } = readExtendedColor(codes, i + 1);
        if (color !== null) {
          next[code === 38 ? "foreground" : "background"] = color;
        }
        i = after - 1;
        break;
      }
      default: {
        break;
      }
    }
  }
  return next;
}

/**
 * Resolves a parsed color to CSS. The 16 standard colors come from `basePalette` (so a theme can
 * keep them legible); 16–231 is the xterm 6×6×6 cube and 232–255 its greyscale ramp.
 */
export function resolveAnsiColor(color: AnsiColor, basePalette: readonly string[]): string {
  if (typeof color === "string") {
    return color;
  }
  if (color < 16) {
    return basePalette[color] ?? basePalette[7] ?? "#808080";
  }
  if (color >= 232) {
    const grey = 8 + (color - 232) * 10;
    return `#${hex(grey)}${hex(grey)}${hex(grey)}`;
  }
  const cube = color - 16;
  const level = (step: number) => CUBE_LEVELS[step] ?? 0;
  return `#${hex(level(Math.floor(cube / 36)))}${hex(level(Math.floor(cube / 6) % 6))}${hex(level(cube % 6))}`;
}

/** Splits terminal-colored text into styled runs, dropping every escape sequence. */
export function parseAnsiText(text: string): AnsiSegment[] {
  const segments: AnsiSegment[] = [];
  let style = PLAIN_STYLE;
  let rest = text;
  const push = (chunk: string) => {
    if (chunk.length > 0) {
      segments.push({ style, text: chunk });
    }
  };
  for (let match = ESCAPE_PATTERN.exec(rest); match !== null; match = ESCAPE_PATTERN.exec(rest)) {
    push(rest.slice(0, match.index));
    rest = rest.slice(match.index + match[0].length);
    const [, params, final] = match;
    if (final === "m" && params !== undefined) {
      style = applySgr(style, params);
    }
  }
  push(rest);
  return segments;
}
